/**
 * Relative time, deep links, type look-up, and today/week/older grouping for notifications.
 */
import type { AppNotification, NotificationType } from '@/types/notification.types';
import {
  NOTIFICATION_BADGE_CAP,
  NOTIFICATION_BUCKET_ORDER,
  NOTIFICATION_TYPE_CONFIG,
  NOTIFICATION_TYPE_FALLBACK,
  type NotificationBucket,
  type NotificationTypeConfig,
} from './notification-configs';

/** Exact count (1–9) vs capped `9+` display. */
export type NotificationBadgeDisplay = { kind: 'exact'; text: string } | { kind: 'capped' };

/** Resolve icon + colors for a notification type. */
export function getNotificationTypeConfig(type: NotificationType): NotificationTypeConfig {
  return NOTIFICATION_TYPE_CONFIG[type] ?? NOTIFICATION_TYPE_FALLBACK;
}

/** Relative time label from an ISO timestamp (minutes / hours / days). */
export function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return date.toLocaleDateString('fr-FR');
}

/** Read a string payload field, or `null` if missing/wrong type. */
function payloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' ? value : null;
}

/** Deep-link to `/vscReports` with view / date / establishment query params. */
function vscReportsLink(payload: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  const dailyId = payloadString(payload, 'vscDailyReportId');
  const reportDate = payloadString(payload, 'reportDate');
  const establishmentId = payloadString(payload, 'establishmentId');
  if (dailyId) qs.set('view', dailyId);
  if (reportDate) qs.set('reportDate', reportDate.slice(0, 10));
  if (establishmentId) qs.set('establishmentId', establishmentId);
  const q = qs.toString();
  return q ? `/vscReports?${q}` : '/vscReports';
}

/** Deep-link to a report (`?view=`) or the session when `reportId` is missing. */
function sessionReportLink(payload: Record<string, unknown>): string | null {
  const sessionId = payloadString(payload, 'sessionId');
  const reportId = payloadString(payload, 'reportId');
  if (sessionId && reportId) {
    const qs = new URLSearchParams();
    qs.set('view', reportId);
    qs.set('sessionId', sessionId);
    return `/reports?${qs.toString()}`;
  }
  if (sessionId) {
    return `/sessions?view=${encodeURIComponent(sessionId)}`;
  }
  return null;
}

/** Route opened when the user clicks a notification. */
export function resolveDeepLink(n: AppNotification): string | null {
  const payload = n.payload ?? {};
  if (n.type === 'VSC_DAILY_REPORT_AVAILABLE') {
    return vscReportsLink(payload);
  }
  if (n.type === 'BINOME_CANCELLATIONS_THRESHOLD_COORDINATOR') {
    const navigation = payload['navigation'];
    const binomeId =
      navigation && typeof navigation === 'object'
        ? (navigation as { binomeId?: string }).binomeId
        : null;
    return binomeId ? `/pairs?view=${encodeURIComponent(binomeId)}` : '/pairs';
  }
  return sessionReportLink(payload);
}

/** Which {@link NotificationBucket} an ISO timestamp falls into (local calendar day). */
export function bucketOf(iso: string): NotificationBucket {
  const date = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (date.getTime() >= startOfToday) return 'today';
  const weekAgo = startOfToday - 6 * 24 * 60 * 60 * 1000;
  if (date.getTime() >= weekAgo) return 'week';
  return 'older';
}

/** Notifications grouped by bucket, omitting empty buckets. */
export function groupByBucket(
  items: AppNotification[],
): Array<[NotificationBucket, AppNotification[]]> {
  const map: Record<NotificationBucket, AppNotification[]> = { today: [], week: [], older: [] };
  for (const it of items) {
    map[bucketOf(it.createdAt)].push(it);
  }
  return NOTIFICATION_BUCKET_ORDER.map((bucket): [NotificationBucket, AppNotification[]] => [
    bucket,
    map[bucket],
  ]).filter(([, arr]) => arr.length > 0);
}

/** Exact 1–{@link NOTIFICATION_BADGE_CAP}; above that show the cap with a highlighted "+". */
export function getNotificationBadgeDisplay(count: number): NotificationBadgeDisplay {
  if (count > NOTIFICATION_BADGE_CAP) return { kind: 'capped' };
  return { kind: 'exact', text: String(count) };
}
